import { coarsePointerNoBackdropBlur } from 'features/knowledgeDocumentViewer/components/viewer/shared/coarsePointerNoBackdropBlur'
import { Loader2 } from 'lucide-react'
import styled, { keyframes } from 'styled-components'

export const EditorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  background-color: var(--card);
  overflow: hidden;
`

export const EditorMainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

export const ViewerAndSidebarContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`

export const LoadingContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

export const ViewerContainer = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;
  border-radius: inherit;
  background-color: var(--card);
`

export const DocumentFullscreenViewerShell = styled(ViewerContainer)<{
  $isDocumentFullscreen: boolean
}>`
  ${({ $isDocumentFullscreen }) =>
    $isDocumentFullscreen &&
    `
    position: fixed;
    inset: 0;
    /* Above homebar (100) and chatbot dock; below toasts. Requires body portal. */
    z-index: 2000;
    border-radius: 0;
  `}
`

const tabFadeIn = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

export const TabTransitionOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
  animation: ${tabFadeIn} 120ms ease-out forwards;
  pointer-events: none;
  z-index: 10;
`

export const ThumbnailSidebarContainer = styled.div<{
  $isOpen: boolean
  $width: number | null
}>`
  width: ${({ $isOpen, $width }) =>
    $isOpen ? ($width ? `${$width}px` : '7rem') : '0'};
  height: 100%;
  min-height: 0;
  flex-shrink: 0;
  overflow: visible;
  transition: ${({ $width }) => ($width ? 'none' : 'width 0.3s ease')};
  border-right: ${({ $isOpen, theme }) =>
    $isOpen ? `1px solid ${theme.forecast.border}` : 'none'};
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  position: relative;
`

export const ViewerWithThumbnailsRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

export const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

export const DocumentContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-top: 0px;
  border-radius: 0 0 var(--radius) var(--radius);
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
`

export const ChatbotPanel = styled.div<{ $isOpen: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? '25rem' : '0')};
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  transition: width 0.3s ease;
  border-left: ${({ $isOpen, theme }) =>
    $isOpen ? `1px solid ${theme.forecast.border}` : 'none'};
`

export const TabContentPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
`

/**
 * KeepAlivePanel: opacity hides inactive tabs immediately (visibility:hidden
 * lags via Button transition-all and can flash descendants).
 */
export const KeepAlivePanel = styled.div.attrs<{ $active: boolean }>(
  ({ $active }) => ({
    'aria-hidden': !$active,
    inert: $active ? undefined : '',
  })
)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  z-index: ${({ $active }) => ($active ? 2 : 0)};
  ${({ $active }) => (!$active ? 'opacity: 0; pointer-events: none;' : '')}
`

export const DocumentContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

export const DocumentUnavailableContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  text-align: center;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
`

export const DocumentUnavailableBody = styled.div`
  max-width: 34rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

export const DocumentUnavailableId = styled.code`
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  word-break: break-all;
`

const pageLoadingSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const PageLoadingSpinner = styled(Loader2)`
  animation: ${pageLoadingSpin} 1s linear infinite;
  color: ${(props) => props.theme.forecast.secondaryText};
`

export const PageLoadingIndicator = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border-radius: 0.25rem;
  background-color: color-mix(
    in oklch,
    ${(props) => props.theme.forecast.background1},
    transparent 20%
  );
  backdrop-filter: blur(2rem);
  -webkit-backdrop-filter: blur(2rem);
  ${coarsePointerNoBackdropBlur}
`
